<template>
  <page layoutClass="manage-users" :loading="false">
    <div class="d-flex flex-row justify-content-between align-items-start">
      <div class="d-flex flex-column justify-content-start">
        <h2>{{ $t('contentShares.title') }}</h2>
        <p v-if="isRecruiter">{{ $t('contentShares.description-recruiter') }}</p>
        <p v-else>{{ $t('contentShares.description-employee') }}</p>
      </div>
      <b-btn v-if="isRecruiter && isEnglishLang" @click.prevent="() => selectedContentShareID = 'NEW'" variant="outline-primary">
        <font-awesome-icon icon="plus"/>
        <span class="d-none d-sm-inline-block">{{ $t('ctas.plus-new') }}</span>
      </b-btn>
    </div>

    <!-- Error -->
    <error-block v-if="allContentSharesErr" class="mb-3" :err="allContentSharesErr"/>

    <!-- Content -->
    <template v-if="contentShareConnections && Array.isArray(contentShareConnections.edges) && contentShareConnections.edges.length">
      <b-row class="card-deck">
        <b-col class="mb-3" cols="12" sm="6" lg="4" xl="3" v-for="({ entry }, i) in contentShareConnections.edges" :key="`item-${entry.id}-${i}`">
          <content-card :entry="entry" @trackShare="data => trackShareEvent(entry, data)">
            <template v-slot:footer v-if="isRecruiter && isEnglishLang">
              <b-card-footer class="d-flex align-items-center">
                <b-btn @click.prevent="() => selectedContentShareID = entry.id" variant="link">{{ $t('contentShares.card.edit-cta') }}</b-btn>
                <div class="flex-fill"/>
                <i18n path="general.added" tag="span">
                  <template v-slot:timeAgo>{{ timeAgo(entry.created) }}</template>
                </i18n>
              </b-card-footer>
            </template>
          </content-card>
        </b-col>
      </b-row>

      <!-- Pagination -->
      <b-card class="bg-transparent border-0" body-class="d-flex flex-row p-3 justify-content-center align-items-center" v-if="contentShareConnections && page && totalPages">
        <b-pagination v-model="filterContentSharesPage" :per-page="limit" :total-rows="total"/>
      </b-card>
    </template>
    <b-card v-else>
      <b-card-title>{{ $t('contentShares.errors.no-active-content') }}</b-card-title>
    </b-card>

    <!-- Edit Modal -->
    <template v-if="isRecruiter && selectedContentShareID">
      <b-modal
        v-if="selectedContentShare && selectedContentShare.id"
        visible
        size="xl"
        :title="contentShareEditorModalTitle"
        no-close-on-backdrop
        hide-footer
        @hidden="onShareModalClosed"
      >
          <error-block v-if="selectedContentShareErr" class="mb-3" :err="selectedContentShareErr"/>
          <content-share-fetch
            :loading="$apollo.queries.prefetchedContentShare.loading"
            :prefetchError="prefetchError"
            :value="{
              url: prefetchURL || selectedContentShare.url,
              ...(prefetchedContentShare || (({ url, ...r }) => r)(selectedContentShare || {})),
            }"
            @fetchContent="fetchContent($event)"
            @dismissPrefetchError="prefetchError = null"
            @input="updateContentShare"
            @disableSelectedContentShare="disableSelectedContentShare"
            @publishSelectedContentShare="publishSelectedContentShare"
            @deleteSelectedContentShare="deleteSelectedContentShare"
          />
      </b-modal>
      <b-modal
        v-else-if="selectedContentShareID === 'NEW'"
        visible
        size="xl"
        :title="contentShareEditorModalTitle"
        no-close-on-backdrop
        hide-footer
        @hidden="onShareModalClosed"
      >
          <error-block v-if="selectedContentShareErr" class="mb-3" :err="selectedContentShareErr"/>
          <content-share-fetch
            :loading="$apollo.queries.prefetchedContentShare.loading"
            :prefetchError="prefetchError"
            :value="{
              url: prefetchURL,
              ...(prefetchedContentShare || {}),
            }"
            @fetchContent="fetchContent($event)"
            @dismissPrefetchError="prefetchError = null"
            @input="createNewContentShare"
          />
      </b-modal>
    </template>

  </page>
</template>

<script>
import _chunk from 'lodash/chunk';
import Page from '@/components/Page';
import ContentCard from '@/components/content-share/ContentCard';
import ContentShareEditorForm from '@/components/content-share/ContentShareEditorForm';
import ContentShareFetch from '@/components/content-share/ContentShareFetch';
import Share from '@/components/Share';
import ContentSharesListQuery from '@/graphql/queries/ContentSharesList.gql';
import PrefetchContentShareQuery from '@/graphql/queries/PrefetchContentShare.gql';
import ContentShareCreateMutation from '@/graphql/mutations/ContentShareCreate.gql';
import ContentShareUpdateMutation from '@/graphql/mutations/ContentShareUpdate.gql';
import ErrorBlock from '@/components/misc/ErrorBlock';
import distanceInWords from 'date-fns/distance_in_words';
import { mapRoot } from '@src/mixins/root';

const { default: confetti } = process.env.VUE_ENV === 'client' ? require('canvas-confetti') : null;

export default {
  components: {
    Page,
    ContentCard,
    ContentShareEditorForm,
    ContentShareFetch,
    ErrorBlock,
    Share,
  },
  mixins: [
    // enumsMixin,
  ],
  data: () => ({
    allContentSharesErr: null,
    selectedContentShareID: null,
    selectedContentShare: null,
    selectedContentShareErr: null,
    filterContentSharesPage: 1,
    filterContentSharesLimit: 12,
    filterContentSharesOrder: 'CREATED_DESC',
    prefetchedContentShare: null,
    prefetchURL: null,
    prefetchError: null,
  }),
  metaInfo() {
    return {
      title: this.$t('contentShares.title'),
    };
  },
  events: {
    capturedLog({ eventName }) {
      if ([ 'JOB_SHARED', 'CONTENT_SHARED' ].includes(eventName)) {
        confetti({ particleCount: 150, spread: 180, zIndex: 1200 });
        if (this.$apollo.queries.dashboard) {
          this.$apollo.queries.dashboard.refresh();
        }
      }
    },
  },
  apollo: {
    contentShareConnections: {
      query: ContentSharesListQuery,
      variables() {
        const { filterContentSharesPage, filterContentSharesLimit, filterContentSharesOrder } = this;
        return {
          filterContentSharesWhere: { status: 'ACTIVE' },
          filterContentSharesPage,
          filterContentSharesLimit,
          filterContentSharesOrder,
        };
      },
      update: ({ company: { contentShareConnections } }) => contentShareConnections,
      error(err) {
        this.allContentSharesErr = err;
      },
    },
    selectedContentShare: {
      query: ContentSharesListQuery,
      variables() {
        const { selectedContentShareID } = this;
        return {
          filterContentSharesWhere: { id: selectedContentShareID, status: 'ACTIVE' },
          filterContentSharesPage: 1,
          filterContentSharesLimit: 1,
        };
      },
      update: ({ company: { contentShareConnections: { edges: [ { entry } = {} ] = [] } = {} } = {} }) => entry,
      error(err) {
        this.selectedContentShareErr = err;
      },
      skip() {
        const { isRecruiter, selectedContentShareID } = this;
        return !isRecruiter || !selectedContentShareID || selectedContentShareID === 'NEW';
      },
      fetchPolicy: 'network-only',
    },
    prefetchedContentShare: {
      query: PrefetchContentShareQuery,
      variables() {
        const { prefetchURL } = this;
        return { url: prefetchURL };
      },
      manual: true,
      result({ data: { company }, loading }) {
        if (!loading) {
          const { contentSharePrefetch } = company || {};
          const { image, ...contentShare } = contentSharePrefetch || {};
          if (!contentShare.title || !contentShare.description) {
            this.prefetchError = 'fetching content was unsuccessful';
          }
          this.prefetchedContentShare = { imageURL: image, ...contentShare };
        }
      },
      error(error) {
        this.prefetchError = error;
      },
      skip() {
        const { isRecruiter, prefetchURL } = this;
        return !isRecruiter || !prefetchURL;
      },
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    ...mapRoot([ 'isRecruiter', 'isEnglishLang' ]),
    contentShareEditorModalTitle() {
      const { selectedContentShareID: id } = this;
      return id ? (id === 'NEW' ? 'Create Content' : 'Edit Content') : null;
    },
    rows() {
      const { edges } = this.contentShareConnections || {};
      if (Array.isArray(edges) && edges.length) {
        return _chunk(edges.map(({ entry }) => entry), 3);
      } else {
        return [];
      }
    },
    page() {
      const { page } = this.contentShareConnections;
      return page || 1;
    },
    limit() {
      const { limit } = this.contentShareConnections;
      return limit || 10;
    },
    total() {
      const { total } = this.contentShareConnections;
      return total || 0;
    },
    totalPages() {
      const { totalPages } = this.contentShareConnections;
      return totalPages || 1;
    },
  },
  methods: {
    trackShareEvent({ id: contentShareID }, { channel }) {
      return this.$root.captureLog({
        eventName: 'CONTENT_SHARED',
        contentShare: { id: contentShareID },
        user: { self: true },
        data: channel ? [ { key: 'channel', value: channel } ] : null,
      });
    },
    onShareModalClosed() {
      this.prefetchURL = null;
      this.prefetchedContentShare = null;
      this.selectedContentShareID = null;
      this.selectedContentShare = null;
      this.selectedContentShareErr = null;
    },
    timeAgo(createdAt) {
      return distanceInWords(new Date(), createdAt, { addSuffix: true });
    },
    async createNewContentShare(create) {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: ContentShareCreateMutation,
          variables: { create },
        });

        if (data && data.createContentShare && data.createContentShare.id) {
          const { title } = data.createContentShare;
          this.$notification({
            title: 'Content created',
            message: `Content with title '${title}' is created successfully`,
            variant: 'success',
          });
        } else {
          throw new Error('Failed to create content');
        }

        await this.$apollo.queries.contentShareConnections.refetch();
        await this.onShareModalClosed();
      } catch (err) {
        console.error(err);
        this.selectedUserErr = err;
      }
    },
    async updateContentShare(update) {
      const { selectedContentShareID: contentShareID } = this;
      if (!contentShareID) {
        return this.$notifyErr(new Error('Missing selectedContentShareID'));
      }

      try {
        const { data } = await this.$apollo.mutate({
          mutation: ContentShareUpdateMutation,
          variables: { contentShareID, update },
        });

        if (data && data.updateContentShare && data.updateContentShare.id) {
          const { title } = data.updateContentShare;
          this.$notification({
            title: 'Content updated',
            message: `Content with title '${title}' is updated successfully`,
            variant: 'success',
          });
        } else {
          throw new Error('Failed to update content');
        }

        await this.$apollo.queries.contentShareConnections.refetch();
        await this.onShareModalClosed();
      } catch (err) {
        console.error(err);
        this.selectedUserErr = err;
      }
    },
    async disableSelectedContentShare() {
      const { selectedContentShareID: contentShareID } = this;
      if (!contentShareID) {
        return this.$notifyErr(new Error('Missing selectedContentShareID'));
      }

      try {
        const { data } = await this.$apollo.mutate({
          mutation: ContentShareUpdateMutation,
          variables: { contentShareID, update: { status: 'INACTIVE' } },
        });

        if (data && data.updateContentShare && data.updateContentShare.id) {
          const { title } = data.updateContentShare;
          this.$notification({
            title: 'Content is archived',
            message: `Content with title '${title}' is archived`,
            variant: 'success',
          });
        } else {
          throw new Error('Failed to archive content');
        }

        await this.$apollo.queries.contentShareConnections.refetch();
        await this.onShareModalClosed();
      } catch (err) {
        console.error(err);
        this.selectedUserErr = err;
      }
    },
    async publishSelectedContentShare() {
      const { selectedContentShareID: contentShareID } = this;
      if (!contentShareID) {
        return this.$notifyErr(new Error('Missing selectedContentShareID'));
      }

      try {
        const { data } = await this.$apollo.mutate({
          mutation: ContentShareUpdateMutation,
          variables: { contentShareID, update: { status: 'ACTIVE' } },
        });

        if (data && data.updateContentShare && data.updateContentShare.id) {
          const { title } = data.updateContentShare;
          this.$notification({
            title: 'Content is published',
            message: `Content with title '${title}' is published`,
            variant: 'success',
          });
        } else {
          throw new Error('Failed to publish content');
        }

        await this.$apollo.queries.contentShareConnections.refetch();
        await this.onShareModalClosed();
      } catch (err) {
        console.error(err);
        this.selectedUserErr = err;
      }
    },
    async deleteSelectedContentShare() {
      const { selectedContentShareID: contentShareID } = this;
      if (!contentShareID) {
        return this.$notifyErr(new Error('Missing selectedContentShareID'));
      }

      try {
        await this.$apollo.mutate({
          mutation: ContentShareUpdateMutation,
          variables: { contentShareID, update: { status: 'DELETED' } },
        });

        this.$notification({
          title: 'Content is deleted',
          message: 'Content is successfully deleted',
          variant: 'success',
        });

        await this.$apollo.queries.contentShareConnections.refetch();
        await this.onShareModalClosed();
      } catch (err) {
        console.error(err);
        this.selectedUserErr = err;
      }
    },
    async fetchContent(url) {
      this.prefetchURL = null;
      if (url === this.prefetchURL) {
        await this.$apollo.queries.prefetchedContentShare.refetch();
        return;
      }
      this.prefetchURL = url;
    }
  },
};
</script>

<style lang="scss">
  .no-content-box {
    padding: 50px 15px;
    text-align: center;

    p {
      font-size: 1.05rem;
    }
  }
</style>



// WEBPACK FOOTER //
// src/views/content-share/ContentShareList.vue